// React
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Material UI
import {
  Box,
  IconButton,
  Typography,
  Container,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  AddRounded,
  CancelRounded,
  SearchRounded,
  ModeEditRounded,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { setAllClients, selectAllClients } from "Store/ClientSlice";

// Styles
import { styles } from "./styles";

// Components
import ClinicManagement from "../ClinicManagement";
import { getAllClients } from "API/Resource/client";

const ClientManagement = () => {
  const dispatch = useDispatch();
  const allClients = useSelector(selectAllClients);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  const clinicManagementRef = useRef(null);
  const navigate = useNavigate();

  const columns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 100,
      flex: 0.8,
    },
    {
      field: "domain",
      headerName: "Domain",
      minWidth: 100,
      flex: 0.8,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => {
        return (
          <Box sx={styles.actionsBox}>
            <Tooltip title="Edit client" placement="top">
              <IconButton
                aria-label="edit"
                onClick={() =>
                  navigate("/EditClient", { state: { client: params.row } })
                }
                sx={{ my: "auto" }}
              >
                <ModeEditRounded />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchClients = async () => {
      if (!allClients || allClients.length === 0) {
        setLoading(true);
        const response = await getAllClients(false);
        dispatch(setAllClients(response));
        setLoading(false);
      } else {
        setLoading(false);
      }
      setClients(allClients || []);
    };
    fetchClients();
  }, [dispatch, allClients]);

  const handleSearch = (event) => {
    const searchValue = event.target.value || "";
    setClientSearch(searchValue);
    filterClients(searchValue);
  };

  const filterClients = (searchValue) => {
    const filtered = allClients.filter((client) =>
      client.name.toLowerCase().includes(searchValue.toLowerCase())
    );
    setClients(filtered);
  };

  const handleClearSearch = () => {
    setClientSearch("");
    setClients(allClients);
  };

  const handleRowClick = (event) => {
    if (event.row.id === selectedClient?.id) {
      setSelectedClient(null);
    } else {
      setSelectedClient(event.row);
      scrollToClinicManagement();
    }
  };

  const scrollToClinicManagement = () => {
    if (clinicManagementRef) {
      clinicManagementRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <Container sx={styles.pageBox}>
      <Box sx={styles.titleBox}>
        <Typography sx={styles.title}>Client Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          sx={styles.addButton}
          onClick={() => navigate("/AddClient")}
        >
          Add Client
        </Button>
      </Box>

      <Container sx={styles.contentBox}>
        <TextField
          name="searchClient"
          placeholder="Search Client"
          type="text"
          variant="standard"
          value={clientSearch}
          onChange={handleSearch}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search bar"
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                  >
                    <CancelRounded fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: 250, ml: "auto" }}
        />
        <DataGrid
          rows={clients}
          columns={columns}
          disableMultipleRowSelection
          rowSelectionModel={selectedClient ? [selectedClient.id] : []}
          onRowClick={handleRowClick}
          rowHeight={60}
          sortingOrder={["asc", "desc"]}
          initialState={{
            sorting: { sortModel: [{ field: "name", sort: "asc" }] },
            pagination: { paginationModel: { pageSize: 5 } },
          }}
          pageSizeOptions={[5]}
          loading={loading}
          sx={styles.datagrid}
        />
      </Container>
      <div ref={clinicManagementRef}>
        <ClinicManagement client={selectedClient} />
      </div>
    </Container>
  );
};

export default ClientManagement;
